import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import AdvanceFileInput from 'components/field/AdvanceFileInput';
import CopyComponent from 'components/field/CopyComponent';
import CreditCard from 'components/field/CreditCard';
import PasswordInput from 'components/field/PasswordInput';
import FormItem from 'components/form/FormItem';
import Accordion from 'components/global/Accordion';
import Alert from 'components/global/Alert';
import Authorization from 'components/global/Authorization';
import Collapse from 'components/global/Collapse';
import Radio from 'components/global/Radio';
import Typography from 'components/global/Typography';
import { ModalContent } from 'components/global/styled/Modal.styled';
import PaytrIframe from 'components/payment/paytr/PaytrIframe';
import TapApplePayButton from 'components/payment/tap/TapApplePayButton';
import TapIframe from 'components/payment/tap/TapIframe';

import useMediaQuery from 'hooks/useMediaQuery';

import { setLoading } from 'stores/layoutSlice';
import { getClientCards } from 'stores/userSlice';

import { Breakpoints } from 'utils/const.styles';
import { PAYMENT_GATEWAY_TYPE, PAYMENT_METHODS, TEST_CARDS } from 'utils/constants';

import * as ModalStyled from '../ChargeAccountModal.styled';
import * as S from './Tabs.styled';

const defaultSpan = { md: 6 };

const SelectPaymentMethod = ({ nextStep, showHeader }) => {
	const dispatch = useDispatch();
	const formMethods = useFormContext();
	const isTablet = useMediaQuery(Breakpoints.tablet);

	const { bankTransferReceiverAccount, minimumBankTransferLimit, bankTransferAvailable, paymentGatewayType } =
		useSelector((state) => state?.user.localization);
	const { clientCards } = useSelector((state) => state?.user);
	const { loading } = useSelector((state) => state.layout);

	const { setValue, watch } = formMethods;

	const isBankCardAvailable = useMemo(
		() => watch('subTotal') >= minimumBankTransferLimit,
		[watch('subTotal'), minimumBankTransferLimit]
	);

	const selectedPaymentMethod = watch('selectedPaymentMethod');

	const radioChange = ({ currentTarget: { id } }) => {
		if (amountWithCODWallet === 0) {
			return;
		}
		if (id === PAYMENT_METHODS.BANK_CARD && !isBankCardAvailable) {
			return;
		}

		if (selectedPaymentMethod === PAYMENT_METHODS.SAVED && id !== PAYMENT_METHODS.SAVED) {
			setValue('selectedSavedCard', { card: null, cvvRequired: false });
		}
		setValue('selectedPaymentMethod', id);
	};

	useEffect(() => {
		if (selectedPaymentMethod === PAYMENT_METHODS.SAVED) {
			setValue('selectedSavedCard', { card: clientCards[0]?.ID, cvvRequired: clientCards[0]?.requireCVV });
		}
	}, [clientCards, selectedPaymentMethod]);

	const savedCardRadioChange = (id, requireCVV) => {
		setValue('selectedSavedCard', { card: Number(id), cvvRequired: requireCVV });
	};

	const getClientCardsOfCompany = useCallback(async () => {
		dispatch(setLoading(true));
		dispatch(getClientCards({}))
			.unwrap()
			.then((result) => {
				if (amountWithCODWallet !== 0) {
					if (result.length > 0) {
						setValue('selectedPaymentMethod', PAYMENT_METHODS.SAVED);
					} else {
						setValue('selectedPaymentMethod', PAYMENT_METHODS.CREDIT_CARD);
					}
				}
			})
			.catch(() => {
				setValue('selectedPaymentMethod', PAYMENT_METHODS.CREDIT_CARD);
			})
			.finally(() => dispatch(setLoading(false)));
	}, []);

	const amountWithCODWallet = useMemo(
		() => watch('total') - watch('codWallet'),
		[watch('total'), watch('codWallet')]
	);

	useEffect(() => {
		getClientCardsOfCompany();
	}, []);

	useEffect(() => {
		if (watch('codWallet') && amountWithCODWallet === 0) {
			setValue('selectedSavedCard', { card: null, cvvRequired: false });
			setValue('selectedPaymentMethod', '');
		}
	}, [amountWithCODWallet]);

	return (
		<ModalContent style={{ paddingBottom: '1rem', width: '100%' }}>
			{!isTablet && showHeader && (
				<ModalStyled.TitleContainer>
					<Typography fontWeight="700" fontSize="var(--paragraph1-font-size)">
						chargeAccount.selectPaymentMethod
					</Typography>
					<Typography variant="caption" color="var(--content-3)">
						chargeAccount.selectPaymentDescription
					</Typography>
				</ModalStyled.TitleContainer>
			)}
			{amountWithCODWallet === 0 && <Alert showIcon content="wholePaymentIsThroughCODWallet" variant="warning" />}
			{clientCards?.length > 0 && (
				<>
					<S.PaymentCard id={PAYMENT_METHODS.SAVED} onClick={radioChange}>
						<Radio
							disabled={amountWithCODWallet === 0}
							checked={selectedPaymentMethod === PAYMENT_METHODS.SAVED}
							readOnly
						/>
						<Typography variant="p1" component="span" fontWeight={500}>
							savedPaymentMethod
						</Typography>
					</S.PaymentCard>
					<Collapse visible={selectedPaymentMethod === PAYMENT_METHODS.SAVED} invisibleMode defaultVisible>
						<S.UserCardsContainer>
							{clientCards?.map((card, index) => (
								<CreditCard
									key={index}
									onClick={() => savedCardRadioChange(card.ID, card?.requireCVV)}
									lastFour={card.cardLastFour}
									cardBrand={card.cardBrand}
									year={card.year}
									month={card.month}
									isSelected={watch('selectedSavedCard')?.card === card.ID}
								/>
							))}
						</S.UserCardsContainer>
						{watch('selectedSavedCard')?.cvvRequired && (
							<FormItem label="selectedSavedCardCvv" colSpan={defaultSpan} isLoading={loading} required>
								<PasswordInput name="cvvForSavedCard" />
							</FormItem>
						)}
					</Collapse>
				</>
			)}
			<S.PaymentCard id={PAYMENT_METHODS.CREDIT_CARD} onClick={radioChange}>
				<Radio
					disabled={amountWithCODWallet === 0}
					checked={selectedPaymentMethod === PAYMENT_METHODS.CREDIT_CARD}
					readOnly
				/>
				<Typography variant="p1" component="span" fontWeight={500}>
					creditcard
				</Typography>
			</S.PaymentCard>

			<Collapse visible={selectedPaymentMethod === PAYMENT_METHODS.CREDIT_CARD} invisibleMode defaultVisible>
				{[PAYMENT_GATEWAY_TYPE.TAP_PAYMENT, PAYMENT_GATEWAY_TYPE.TAP_PAYMENT_UAE].includes(
					paymentGatewayType
				) && (
					<S.CreditCardIFrameContainer>
						<TapIframe setInputsValid={(isValid) => setValue('isTapInputsValid', isValid)} />
					</S.CreditCardIFrameContainer>
				)}

				{paymentGatewayType === PAYMENT_GATEWAY_TYPE.PAYTR && (
					<S.CreditCardContainer>
						<PaytrIframe loading={loading} />
					</S.CreditCardContainer>
				)}
				{import.meta.env.VITE_SANDBOX === 'true' && (
					<S.CreditCardIFrameContainer className="mt-4">
						<Alert content="sandboxArea" showIcon variant="info" />
						<Accordion header="testCards" className="mt-3">
							<S.TestCardContainer>
								{TEST_CARDS?.[paymentGatewayType]?.map((card, index) => (
									<S.TestCard key={index}>
										<Typography fontWeight={600} className="pb-3">
											<Trans
												i18nKey="chargeAccount.testCard"
												values={{
													count: index + 1
												}}
											/>
										</Typography>
										<S.TestCardRow>
											<Typography fontWeight={500}>cardNumber</Typography>
											<CopyComponent value={card.cardNumber} className="copy-area" />
										</S.TestCardRow>
										<S.TestCardRow>
											<Typography fontWeight={500}>expiry</Typography>
											<CopyComponent
												value={`${card.expiryMonth}/${card.expiryYear}`}
												className="copy-area"
											/>
										</S.TestCardRow>
										<S.TestCardRow>
											<Typography fontWeight={500}>cvv</Typography>
											<CopyComponent value={card.cvv} className="copy-area" />
										</S.TestCardRow>
										<S.TestCardRow>
											<Typography fontWeight={500}>cardHolderName</Typography>
											<CopyComponent value={card.cardHolderName} className="copy-area" />
										</S.TestCardRow>
									</S.TestCard>
								))}
							</S.TestCardContainer>
						</Accordion>
					</S.CreditCardIFrameContainer>
				)}
			</Collapse>
			{[PAYMENT_GATEWAY_TYPE.TAP_PAYMENT, PAYMENT_GATEWAY_TYPE.TAP_PAYMENT_UAE].includes(paymentGatewayType) &&
				window.ApplePaySession && (
					<Authorization path="/charge-account/applePay">
						<S.PaymentCard id={PAYMENT_METHODS.APPLE_PAY} onClick={radioChange}>
							<Radio
								disabled={amountWithCODWallet === 0}
								checked={selectedPaymentMethod === PAYMENT_METHODS.APPLE_PAY}
								readOnly
							/>
							<Typography variant="p1" component="span" fontWeight={500}>
								applePay
							</Typography>
						</S.PaymentCard>
						<Collapse visible={selectedPaymentMethod === PAYMENT_METHODS.APPLE_PAY} defaultVisible>
							<TapApplePayButton
								nextStep={nextStep}
								total={watch('total')}
								subTotal={watch('subTotal')}
								promoCode={watch('promoCode')}
							/>
						</Collapse>
					</Authorization>
				)}

			{bankTransferAvailable && bankTransferReceiverAccount && (
				<>
					<S.PaymentCard id={PAYMENT_METHODS.BANK_CARD} onClick={radioChange}>
						<Radio
							checked={selectedPaymentMethod === PAYMENT_METHODS.BANK_CARD}
							readOnly
							disabled={!isBankCardAvailable || amountWithCODWallet === 0}
						/>
						<Typography variant="p1" component="span" fontWeight={500}>
							ibanPaymentHeader
						</Typography>
					</S.PaymentCard>
					{!isBankCardAvailable && amountWithCODWallet !== 0 && (
						<Typography variant="caption" color="var(--content-3)">
							<Trans
								i18nKey="paymentMethod.minimumBankTransferLimit"
								components={{
									blackText: <S.BlackText />
								}}
							/>
						</Typography>
					)}

					<Collapse visible={selectedPaymentMethod === PAYMENT_METHODS.BANK_CARD} invisibleMode>
						<S.BankCardContainer>
							<Alert content={<Trans i18nKey="creditContentText" />} variant="warning" showIcon />

							<S.BankDetails>
								<S.BankDetailsRow>
									<Typography fontWeight={500}>bankName</Typography>
									<Typography>{watch('bankName')}</Typography>
								</S.BankDetailsRow>
								<S.BankDetailsRow>
									<Typography fontWeight={500}>bankHolderName</Typography>
									<CopyComponent value={watch('bankHolderName')} className={'bank-copy-area'} />
								</S.BankDetailsRow>
								<S.BankDetailsRow>
									<Typography fontWeight={500}>ibanNumber</Typography>
									<CopyComponent value={watch('ibanNumber')} className={'bank-copy-area'} />
								</S.BankDetailsRow>
							</S.BankDetails>

							<FormItem
								colSpan={defaultSpan}
								required
								label={'receiptUpload'}
								className="receipt-upload"
								style={{ width: '100%', padding: 0 }}>
								<AdvanceFileInput
									name="receipt"
									style={{ width: '100%' }}
									btnStyle={{ width: '100%' }}
									acceptedFileType={'.pdf'}
									setValue={(value) => setValue('receipt', value)}
									value={
										watch('receipt') || {
											name: null,
											path: null
										}
									}
									maxFileSize={1000000}
								/>
							</FormItem>
						</S.BankCardContainer>
					</Collapse>
				</>
			)}
		</ModalContent>
	);
};

export default SelectPaymentMethod;
